import React, { useEffect } from 'react';
import DaApp from '@wix/da-shared-react/pkg/DaApp';
import { RuntimeEnvironment } from '@wix/da-shared-react/pkg/types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { isPublished } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import SharedDeviationPage from '@wix/da-shared-react/pkg/DeviationPage';
import SharedStashPage from '@wix/da-shared-react/pkg/DeviationPage/StashPage';
import { useScrollRestore } from '@wix/da-shared-react/pkg/Duperbrowse/hooks/useScrollRestore';
import DrawerLoadable from '@wix/da-feedback-shared/pkg/Drawer/DrawerLoadable';
import FeedbackPageLoadable from '@wix/da-feedback-shared/pkg/FeedbackPage/FeedbackPageLoadable';
import { DuperbrowseViewContext } from '@wix/da-shared-react/pkg/Duperbrowse/DuperbrowseViewContext';
import { PapiDeviation } from '@wix/da-papi-types';
import { composeModalRenderers } from '@wix/da-shared-react/pkg/Modals/helpers';
import { feedbackModalRenderer } from '@wix/da-feedback-shared/pkg/project-tools';
import { adoptsModalRenderer } from '@wix/da-adopts-shared/pkg/project-tools';
import { submitModalRenderer } from '@wix/da-submit-shared/pkg/project-tools';
import PWAInstallBanner from '@wix/da-shared-react/pkg/PWAInstallBanner';
import { MuroDepContext } from '@wix/da-react-context/pkg/MuroDepContext';
import MicroMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Micro';
import MiniMuro from '@wix/da-muro-ui/pkg/components/MuroEditor/preset/Mini';

const MuroDepValue = { MicroComponent: MicroMuro, MiniComponent: MiniMuro };

export interface Props {
  language: string;
  deviation?: PapiDeviation;
  isFeedbackPageOpen?: boolean;
  environment?: RuntimeEnvironment;
  PWAInstallBannerVisible?: boolean;
  isStash?: boolean;
  appLoaded: () => void;
}

const App: React.FC<Props> = ({
  language,
  deviation,
  isFeedbackPageOpen,
  environment,
  appLoaded,
  isStash,
  PWAInstallBannerVisible,
}) => {
  useEffect(() => {
    appLoaded();
  }, [appLoaded]);

  useScrollRestore(!!isFeedbackPageOpen);

  // isStash is based on the original route in (server/routes/stash.tsx), but
  // we need it to be false if we're rendering published deviations. It can
  // incorrectly remain true if we loaded the first page with a stash route
  // but then loaded published deviations in duperbrowse (e.g. from the sidebar)
  // so, we check both isStash -and- whether it's unpublished
  const deviationIsUnpublished = deviation && !isPublished(deviation);

  return (
    <MuroDepContext.Provider value={MuroDepValue}>
      <DaApp
        language={language}
        environment={environment}
        isDuperbrowseEnabled={false}
        disablePageWideNotifications
        disableHeadTags={true}
        ncContextProps={{
          PageComponent: FeedbackPageLoadable,
          DrawerComponent: DrawerLoadable,
        }}
        modalsRenderFunc={composeModalRenderers(
          feedbackModalRenderer,
          adoptsModalRenderer,
          submitModalRenderer
        )}
      >
        <ErrorBoundary debugComponent="DeviationApp">
          {isFeedbackPageOpen ? (
            <FeedbackPageLoadable />
          ) : (
            <DuperbrowseViewContext.Provider value="fullscreen">
              {isStash && deviationIsUnpublished ? (
                <SharedStashPage deviation={deviation} />
              ) : (
                <SharedDeviationPage deviation={deviation} />
              )}
            </DuperbrowseViewContext.Provider>
          )}
          {PWAInstallBannerVisible && <PWAInstallBanner />}
        </ErrorBoundary>
      </DaApp>
    </MuroDepContext.Provider>
  );
};
App.displayName = 'App';

export default App;
